import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { InfoOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import { useFormFieldValue } from '@/components/Form';
import FormLayout from '@/components/Form/components/FormLayout';
import NumberInput from '@/components/NumberInput';

import {
  ABSENT_THRESHOLD_DEFAULT_VALUE,
  COMPLETION_FIELD_NAME,
  COMPLETION_THRESHOLD_DEFAULT_VALUE,
  INFO_ICON_FILL,
} from './consts';

interface EventAttendanceSettingsProps {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
  error: any;
  disabled: boolean;
}

function EventAttendanceSettings({ setFieldValue, error, disabled }: EventAttendanceSettingsProps) {
  const { t } = useTranslation();
  const currentValue = useFormFieldValue(COMPLETION_FIELD_NAME);
  const {
    attendanceRecordingCompletionThreshold = COMPLETION_THRESHOLD_DEFAULT_VALUE,
    attendanceRecordingAbsentThreshold = ABSENT_THRESHOLD_DEFAULT_VALUE,
  } = currentValue;

  const handleAbsentThresholdChange = useCallback(
    (newValue: number) => {
      setFieldValue(COMPLETION_FIELD_NAME, {
        ...currentValue,
        attendanceRecordingAbsentThreshold: newValue,
      });
    },
    [setFieldValue, currentValue],
  );

  const handleCompletionThresholdChange = useCallback(
    (newValue: number) => {
      setFieldValue(COMPLETION_FIELD_NAME, {
        ...currentValue,
        attendanceRecordingCompletionThreshold: newValue,
      });
    },
    [setFieldValue, currentValue],
  );

  return (
    <FormLayout>
      <FormLayout.Title>
        {t('Events:Editor.AttendanceSettings.AttendanceSettings')}
      </FormLayout.Title>

      <FormLayout.Description>
        <Typography variant="body-short-01">
          {t('Events:Editor.AttendanceSettings.FieldDescription')}
        </Typography>
      </FormLayout.Description>

      <Stack alignItems="center" direction="col" gap={12} justifyContent="center">
        <Stack className="w-full grid grid-cols-2" direction="row" gap={8}>
          <Stack className="flex-1" direction="col" gap={8}>
            <Typography className="ms-8" variant="body-short-02">
              {t('Events:Editor.AttendanceSettings.AbsentLabel')}
            </Typography>
            <NumberInput
              className={cn('w-full')}
              disabled={disabled}
              inputSuffix={t('Events:Editor.AttendanceSettings.Suffix')}
              max={100}
              min={0}
              value={attendanceRecordingAbsentThreshold}
              onChange={handleAbsentThresholdChange}
            />
          </Stack>

          <Stack className="flex-1" direction="col" gap={8}>
            <Typography className="ms-8" variant="body-short-02">
              {t('Events:Editor.AttendanceSettings.PresentLabel')}
            </Typography>
            <NumberInput
              className={cn('w-full')}
              disabled={disabled}
              inputSuffix={t('Events:Editor.AttendanceSettings.Suffix')}
              max={100}
              min={0}
              value={attendanceRecordingCompletionThreshold}
              onChange={handleCompletionThresholdChange}
            />
          </Stack>
        </Stack>

        <Stack className="px-16 py-12 bg-bg-tertiary rounded-8 w-full" direction="col" gap={12}>
          <Stack alignItems="center" direction="row" justifyContent="start">
            <InfoOutline fill={INFO_ICON_FILL} height={16} width={16} />
            <Typography className="ml-8" color="text-text-paragraph" variant="body-long-02">
              {t('Events:Editor.AttendanceSettings.Info1', {
                min: attendanceRecordingAbsentThreshold,
                max: attendanceRecordingCompletionThreshold,
              })}
            </Typography>
          </Stack>

          <Stack alignItems="center" direction="row" justifyContent="start">
            <InfoOutline fill={INFO_ICON_FILL} height={16} width={16} />
            <Typography className="ml-8" color="text-text-paragraph" variant="body-long-02">
              {t('Events:Editor.AttendanceSettings.Info2')}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {error ? (
        <Typography className="mt-8 ms-8" color="text-text-error" variant="body-short-03">
          {error}
        </Typography>
      ) : null}
    </FormLayout>
  );
}

export default React.memo(EventAttendanceSettings);
